import { z } from 'zod';

export const ITEM_IDS = [
  // ── Phase 3 baseline (14 items, preserved) ────────────────────────────
  'scrap_inode',
  'crumb_of_swap',
  'heisenbug_shard',
  'reroll_token',
  'chmod644_scroll',
  'sudo_stub',
  'retry_after_header',
  'coin_429',
  'honey_token',
  'ship_it_feather',
  'polished_ladle',
  'tea_tray_shield',
  'butler_gloves',
  'pressed_waistcoat',
  // ── Skill drops (42-skill expansion) ───────────────────────────────────
  // Combat
  'intent_frag',
  'sentry_fingerprint',
  'summary_scroll',
  'triage_token',
  'hmac_seal',
  'stage_pass',
  'citation_scar',
  'ticket_tentacle',
  'out_of_scope_ink',
  'royal_badge',
  'human_only_seal',
  // Gathering
  'camera_sample',
  'alpr_plate_scrap',
  'specimen_tag',
  'leaderboard_fin',
  'latency_pearl',
  'foia_scrap',
  'public_foia_scrap',
  'rotation_chip',
  // Crafting
  'frontmatter',
  'curator_seal',
  'plugin_rune',
  'screenshot_plate',
  'branch_bar',
  'cron_shard',
  'dead_man_switch',
  'prompt_shard',
  'distilled_image',
  'tempered_yaml',
  // Artisan
  'sonnet_scroll',
  'imagine_tile',
  'hud_frame',
  'page_brick',
  'diagram_brush',
  'voice_cloth',
  // Support
  'motion_scroll',
  'force_push_forbidden_tablet',
  'no_self_restart_engraving',
  'pliers_of_clean',
  'route_lock',
  'launchagent_rune',
  'slack_meter',
  'green_job_table',
  'black_card',
  'unplayable_white',
  'watch_lantern',
  // ── Monster drops (30-monster expansion) ───────────────────────────────
  // Tier 1
  'orphan_pid',
  'defunct_badge',
  'broken_link',
  'soft_404_dust',
  'etag_fragment',
  'cache_cookie',
  'unused_import',
  'semicolon_of_shame',
  'conflict_marker',
  'resolution_glue',
  'lockfile_fang',
  'cve_scarab',
  'last_breath_log',
  'cgroup_tag',
  // Tier 2
  'crontab_shard',
  'burnt_token_ash',
  'soft_cap_charm',
  'hmac_secret_redacted',
  'delivery_attempt',
  'fingerprint_dust',
  'alert_fatigue',
  'artifact_bone',
  'job_badge_red',
  'truncated_memory',
  'expired_jwt',
  'refresh_rune',
  'enospc_scale',
  'tmpfs_eye',
  'watchdog_scale',
  'sigkill_fang',
  // Tier 3
  'confident_wrongness',
  'motion_gavel',
  'table_outcome',
  'fake_crown',
  'apple_silicon_core',
  'bracket_status_relic',
  'removed_persona_ash',
] as const;

/** The 13 locked equipment slots (Phase 2 contract). */
export const EQUIP_SLOT_IDS = [
  'head',
  'chest',
  'legs',
  'feet',
  'hands',
  'cape',
  'neck',
  'ring',
  'gem',
  'weapon',
  'shield',
  'ammo',
  'consumable',
] as const;
export type EquipSlotId = (typeof EQUIP_SLOT_IDS)[number];
export const SKILL_IDS = [
  // Combat (10)
  'prompt_parrying',
  'rate_limit_fencing',
  'sentry_summoning',
  'context_compression',
  'issue_triage',
  'webhook_binding',
  'pipeline_siege',
  'hallucination_slaying',
  'scope_discipline',
  'crown_defense',
  // Gathering (8)
  'token_mining',
  'log_trawling',
  'disk_scavenging',
  'sheep_herding',
  'model_specimen_hunting',
  'benchmark_farming',
  'foia_foraging',
  'secret_prospecting',
  // Crafting (7)
  'skill_authoring',
  'plugin_smithing',
  'mr_forging',
  'cron_weaving',
  'prompt_engineering',
  'container_distilling',
  'config_tempering',
  // Artisan (7)
  'codex_whispering',
  'claude_singing',
  'grok_painting',
  'pixel_butlery',
  'hugo_masonry',
  'excalidraw_illumination',
  'voice_binding',
  // Support (10)
  'parliament_orating',
  'git_lawyering',
  'memory_curating',
  'gateway_binding',
  'housekeeping',
  'peer_routing',
  'quota_stewardship',
  'verification_rituals',
  'cards_against_hermanity',
  'night_watch',
] as const;

export const MONSTER_IDS = [
  'free_disk_roach',
  'flaky_test_gremlin',
  'permission_denied_goblin',
  'rate_limit_hydra',
  'mr_sparks_parakeet',
] as const;
export const NPC_IDS = [
  'mr_sparks',
  'quill',
  'ledger',
  'apex',
  'harrow',
  'bash_bunny',
  'archivist',
  'hatch',
  'sagikos',
  'removed_persona',
] as const;
const PARLIAMENT_NPC_IDS = ['quill', 'ledger', 'apex', 'harrow'] as const;

const ItemIdSchema = z.enum(ITEM_IDS);
const SkillIdSchema = z.enum(SKILL_IDS);
const MonsterIdSchema = z.enum(MONSTER_IDS);
const NpcIdSchema = z.enum(NPC_IDS);

function validateEntries(
  entries: ReadonlyArray<{ id: string }>,
  requiredIds: readonly string[],
  collection: string,
  entryType: string,
  context: z.RefinementCtx,
): void {
  const seen = new Set<string>();

  entries.forEach((entry, index) => {
    if (seen.has(entry.id)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: [collection, index, 'id'],
        message: `Duplicate ${entryType} entry "${entry.id}"`,
      });
    }
    seen.add(entry.id);
  });

  for (const id of requiredIds) {
    if (!seen.has(id)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: [collection],
        message: `Missing required ${entryType} entry "${id}"`,
      });
    }
  }
}

const SkillDropSchema = z.object({
  itemId: ItemIdSchema,
  weight: z.number().positive(),
  minLevel: z.number().int().nonnegative(),
}).strict();

const SkillSchema = z.object({
  id: SkillIdSchema,
  name: z.string().min(1),
  ring: z.enum(['combat', 'gathering', 'crafting', 'artisan', 'support']),
  baseIntervalSec: z.number().positive(),
  baseXpPerTick: z.number().positive(),
  drops: z.array(SkillDropSchema),
  flavor: z.string(),
}).strict();

const SkillsCatalogueSchema = z.object({
  schemaVersion: z.literal(1),
  skills: z.array(SkillSchema),
}).strict().superRefine(({ skills }, context) => {
  validateEntries(skills, SKILL_IDS, 'skills', 'skill', context);
});

const MonsterDropSchema = z.object({
  itemId: ItemIdSchema,
  weight: z.number().positive(),
  qtyMin: z.number().int().positive(),
  qtyMax: z.number().int().positive(),
}).strict().refine((drop) => drop.qtyMax >= drop.qtyMin, {
  path: ['qtyMax'],
  message: 'Must be greater than or equal to qtyMin',
});

const MonsterSchema = z.object({
  id: MonsterIdSchema,
  name: z.string().min(1),
  tier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  zone: z.literal('codex_quarry'),
  hp: z.number().int().positive(),
  attack: z.number().int().nonnegative(),
  defence: z.number().int().nonnegative(),
  attackIntervalSec: z.number().positive(),
  damageToMonster: z.number().int().nonnegative(),
  damageToPlayer: z.number().int().nonnegative(),
  drops: z.array(MonsterDropSchema),
  flavor: z.string(),
  portrait: z.string().min(1),
}).strict();

const MonstersCatalogueSchema = z.object({
  schemaVersion: z.literal(1),
  monsters: z.array(MonsterSchema),
}).strict().superRefine(({ monsters }, context) => {
  validateEntries(monsters, MONSTER_IDS, 'monsters', 'monster', context);
});

const ParliamentSeatSchema = z.object({
  order: z.number().int().positive(),
  displayRole: z.string().min(1),
}).strict();

const NpcSchema = z.object({
  id: NpcIdSchema,
  name: z.string().min(1),
  role: z.string().min(1),
  portrait: z.string().min(1).nullable(),
  motto: z.string().min(1).nullable(),
  parliamentSeat: ParliamentSeatSchema.nullable(),
  dialogues: z.record(z.string().min(1), z.string().min(1)),
}).strict();

const NpcsCatalogueSchema = z.object({
  schemaVersion: z.literal(1),
  npcs: z.array(NpcSchema),
}).strict().superRefine(({ npcs }, context) => {
  validateEntries(npcs, NPC_IDS, 'npcs', 'NPC', context);

  const seatOrders = new Map<number, number>();
  npcs.forEach((npc, index) => {
    const requiresSeat = PARLIAMENT_NPC_IDS.some((id) => id === npc.id);
    if (npc.parliamentSeat === null) {
      if (requiresSeat) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['npcs', index, 'parliamentSeat'],
          message: `Missing Parliament seat metadata for "${npc.id}"`,
        });
      }
      return;
    }
    if (npc.portrait === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['npcs', index, 'portrait'],
        message: `Parliament seat "${npc.id}" requires a portrait`,
      });
    }
    if (npc.motto === null) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['npcs', index, 'motto'],
        message: `Parliament seat "${npc.id}" requires a motto`,
      });
    }
    const previousIndex = seatOrders.get(npc.parliamentSeat.order);
    if (previousIndex !== undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['npcs', index, 'parliamentSeat', 'order'],
        message: `Duplicate Parliament seat order ${npc.parliamentSeat.order}; first used at npcs[${previousIndex}]`,
      });
    }
    seatOrders.set(npc.parliamentSeat.order, index);
  });
});

export type SkillId = z.infer<typeof SkillIdSchema>;
export type SkillDefinition = z.infer<typeof SkillSchema>;
export type SkillsCatalogue = z.infer<typeof SkillsCatalogueSchema>;
export type MonsterId = z.infer<typeof MonsterIdSchema>;
export type MonsterDefinition = z.infer<typeof MonsterSchema>;
export type MonstersCatalogue = z.infer<typeof MonstersCatalogueSchema>;
export type NpcId = z.infer<typeof NpcIdSchema>;
export type NpcDefinition = z.infer<typeof NpcSchema>;
export type NpcsCatalogue = z.infer<typeof NpcsCatalogueSchema>;


function formatPath(path: Array<string | number>): string {
  return path.reduce<string>((result, part) => {
    if (typeof part === 'number') return `${result}[${part}]`;
    return result.length === 0 ? part : `${result}.${part}`;
  }, '');
}

function parseCatalogue<T>(schema: z.ZodType<T>, data: unknown, source: string): T {
  const result = schema.safeParse(data);
  if (result.success) return result.data;

  const diagnostics = result.error.issues
    .map((issue) => `  - ${formatPath(issue.path) || '<root>'}: ${issue.message}`)
    .join('\n');
  throw new Error(
    `[Hermanity Idle] ${source} failed catalogue validation:\n${diagnostics}\n\n` +
      'Aborting — fix the JSON or update the schema.',
  );
}

export function parseSkillsCatalogue(
  data: unknown,
  source = 'skills.json',
): SkillsCatalogue {
  return parseCatalogue(SkillsCatalogueSchema, data, source);
}

export function parseMonstersCatalogue(
  data: unknown,
  source = 'monsters.json',
): MonstersCatalogue {
  return parseCatalogue(MonstersCatalogueSchema, data, source);
}

export function parseNpcsCatalogue(
  data: unknown,
  source = 'npcs.json',
): NpcsCatalogue {
  return parseCatalogue(NpcsCatalogueSchema, data, source);
}
